import { DateUtility } from '@utility/date';
import { theme } from '@utility/theme';
import { Link } from 'react-router';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useState,
  useCallback,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { CaretDown, CaretRight, Truck } from '@phosphor-icons/react';
import { Checkbox, cn } from '@skupreme/shared/ui';
import { MuteText } from '@elements/Common';
import {
  differenceInDays,
  NameContainer,
  Circle,
  BarName,
  ShipmentBar,
} from '@page-components/timeline/common';
import { CommonUtility } from '@utility/common';
import { LightPopup } from '@elements/Popup';

const predictionColor = '#ACADB6';

/* Translucent fill over an opaque surface: the sticky name column must hide
   the bars scrolling beneath it. */
const STICKY_FILL =
  'sk:bg-surface sk:bg-[linear-gradient(var(--sk-fill-tertiary),var(--sk-fill-tertiary))]';

const CONNECTOR =
  'sk:absolute sk:select-none sk:transition-all sk:duration-300 sk:ease-in-out';

type ConnectorProps = {
  left: number;
  spaceFromTop: number;
  color: string;
  opacity?: number;
};

function VerticalLine({
  left,
  spaceFromTop,
  color,
  opacity,
  height,
}: ConnectorProps & { height: number }) {
  return (
    <div
      className={cn(CONNECTOR, 'sk:z-100')}
      style={{
        left,
        width: 3,
        height,
        marginTop: spaceFromTop,
        background: color,
        opacity,
      }}
    />
  );
}

function HorizontalLine({
  left,
  spaceFromTop,
  color,
  opacity,
  width,
}: ConnectorProps & { width: number }) {
  return (
    <div
      className={CONNECTOR}
      style={{
        left,
        width,
        height: 3,
        marginTop: spaceFromTop,
        background: color,
        opacity,
      }}
    />
  );
}

function ArrowHead({
  left,
  spaceFromTop,
  color,
  opacity,
  up = false,
}: ConnectorProps & { up?: boolean }) {
  return (
    <div
      className={cn(CONNECTOR, 'sk:z-100 sk:size-0 sk:border-solid')}
      style={{
        left,
        marginTop: spaceFromTop,
        opacity,
        borderColor: 'transparent',
        borderWidth: up ? '0 5px 5px 5px' : '7px 5px 0 5px',
        ...(up ? { borderBottomColor: color } : { borderTopColor: color }),
      }}
    />
  );
}

function TimelineRow({
  width,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { width: number }) {
  return (
    <div
      {...rest}
      draggable={false}
      className={cn('sk:flex', className)}
      style={{ width }}
    />
  );
}

function BarContainer({
  opacity,
  children,
}: {
  opacity: number;
  children: ReactNode;
}) {
  return (
    <div
      className="sk:relative sk:w-[calc(100%-208px)] sk:transition-all sk:duration-300 sk:ease-in-out"
      style={{ opacity }}
    >
      {children}
    </div>
  );
}

function FullText({ children }: { children: ReactNode }) {
  return (
    <div className="fulltext-popup sk:absolute sk:-top-7.5 sk:left-0 sk:z-120 sk:min-w-fit sk:rounded-[5px] sk:bg-page sk:px-2.5 sk:py-0.5 sk:whitespace-nowrap sk:text-fg">
      {children}
    </div>
  );
}

const nameClass = (checked: boolean) =>
  cn(
    'sk:relative sk:text-xs sk:select-none sk:hover:underline',
    checked ? 'sk:text-fg' : 'sk:text-fg-tertiary',
  );

function ClipTextShipment({ text }) {
  const maxLength = 24;
  const nameRenderer = () => {
    if (text.length <= maxLength) {
      return <span>{text}</span>;
    }
    return <span>{`${text.slice(0, maxLength)}...`}</span>;
  };
  return (
    <LightPopup
      trigger={<span>{nameRenderer()}</span>}
      padding="2px 10px"
      basic
    >
      {text}
    </LightPopup>
  );
}

export function ShipmentRowRenderer({
  last,
  markRendered,
  name = '',
  DayWidth,
  bars,
  differenceInDates,
  checked,
  toggleCheck,
  expanded,
  showShipmentCheckbox,
  minDate,
  toggleExpand,
  isPredictionOnly,
  getBarColor,
  origin_id,
  destination_id,
  ...props
}) {
  const color = useMemo(
    () => getBarColor(`${origin_id}-${destination_id}`),
    [name],
  );
  const { maxCollDate, minCollDate } = useMemo(() => {
    let maxDate = bars[0].ETA;
    let minDate = bars[0].ETD;

    bars.forEach(bar => {
      if (DateUtility.isDateAfter(minDate, bar.ETD)) {
        minDate = bar.ETD;
      }
      if (DateUtility.isDateAfter(bar.ETA, maxDate)) {
        maxDate = bar.ETA;
      }
    });
    return { maxCollDate: maxDate, minCollDate: minDate };
  }, [name]);

  useEffect(() => {
    if (!last) return;
    markRendered();
  }, [last]);

  const collectionFromLeft = useMemo(
    () => differenceInDays(new Date(minDate), new Date(minCollDate)) * DayWidth,
    [minCollDate, minDate],
  );
  const collectionWidth = useMemo(
    () =>
      differenceInDays(new Date(minCollDate), new Date(maxCollDate)) * DayWidth,
    [maxCollDate],
  );

  return (
    <>
      <TimelineRow width={differenceInDates * DayWidth + 208}>
        <div
          className={cn(
            'shipment-name sk:sticky sk:left-0 sk:z-121 sk:flex sk:min-h-[42px] sk:max-h-[42px] sk:min-w-[208px] sk:max-w-[208px] sk:cursor-pointer sk:items-center sk:gap-x-1.5 sk:pb-[7px] sk:pl-2.5 sk:select-none',
            STICKY_FILL,
          )}
        >
          {showShipmentCheckbox ? (
            <Checkbox
              aria-label={`Include ${name}`}
              isSelected={!!checked}
              onChange={() => toggleCheck(!checked)}
            />
          ) : (
            <div className="sk:pl-4">
              <Truck size={18} />
            </div>
          )}
          {bars.length === 1 ? (
            <Link to={bars[0].link || `/app/scm/shipments/${bars[0]._id}`}>
              <span className={cn('shipment-name', nameClass(checked))}>
                {isPredictionOnly ? <ClipTextShipment text={name} /> : name}
                {bars.length > 1 && <>&nbsp;({bars.length})</>}
                {isPredictionOnly ? (
                  <span className="sk:ml-1">(Predicted)</span>
                ) : (
                  ''
                )}
              </span>
            </Link>
          ) : (
            <span className={cn('shipment-name', nameClass(checked))}>
              {name}
              {bars.length > 1 && <MuteText>&nbsp;({bars.length})</MuteText>}
            </span>
          )}
          {bars.length > 1 && (
            <>
              {expanded ? (
                <CaretDown onClick={() => toggleExpand(!expanded)} />
              ) : (
                <CaretRight onClick={() => toggleExpand(!expanded)} />
              )}
            </>
          )}
        </div>
        {bars.length === 1 && (
          <BarContainer opacity={checked ? 1 : 0.3}>
            <ShipmentBarRenderer
              // @ts-expect-error - TS2322: Type '{ title: any; color: any; DayWidth: any; data: any; expanded: any; barInde...
              title={bars[0].title}
              color={color}
              DayWidth={DayWidth}
              data={bars[0]}
              expanded={expanded}
              barIndex={0}
              totalSiblings={bars.length}
              minDate={minDate}
              {...props}
            />
          </BarContainer>
        )}
      </TimelineRow>
      {bars.length > 1 && !expanded && (
        <div
          role="presentation"
          onClick={() => toggleExpand(!expanded)}
          className="sk:absolute sk:z-1 sk:-mt-[3px] sk:-ml-[3px] sk:cursor-pointer sk:rounded-[3px] sk:bg-fill-tertiary sk:select-none"
          style={{
            left: collectionFromLeft + 208,
            width: collectionWidth + 6,
            height: expanded ? bars.length * 28 + 6 : bars.length * 12 + 6,
          }}
        />
      )}
      {bars.length > 1 &&
        !expanded &&
        bars.map((bar, j) => (
          <TimelineRow width={differenceInDates * DayWidth + 208}>
            <div
              className={cn(
                'shipment-name sk:sticky sk:left-0 sk:z-121 sk:flex sk:min-h-3 sk:max-h-3 sk:min-w-[208px] sk:max-w-[208px] sk:cursor-pointer sk:items-center sk:gap-x-1.5 sk:pb-1 sk:pl-6 sk:select-none',
                STICKY_FILL,
              )}
            />
            <BarContainer opacity={checked ? 1 : 0.3}>
              {/* @ts-expect-error - TS2740: Type '{ HeightBar: number; showTitle: false; color: any; Day... */}
              <ShipmentBarRenderer
                showTitle={false}
                color={color}
                DayWidth={DayWidth}
                data={bar}
                expanded={expanded}
                barIndex={j}
                totalSiblings={bars.length}
                minDate={minDate}
                {...props}
                HeightBar={8}
              />
            </BarContainer>
          </TimelineRow>
        ))}
      {expanded &&
        bars.map((bar, j) => {
          const shipmentColor =
            bar?.status === 'prediction'
              ? theme.colors.yellow
              : bar.uniqueColor || color;

          return (
            <TimelineRow
              className="sk:cursor-pointer"
              onClick={() => toggleExpand(!expanded)}
              width={differenceInDates * DayWidth + 208}
            >
              <NameContainer className="shipment-name sk:pl-6">
                <Circle bgColor={shipmentColor} />
                <Link to={`/app/scm/shipments/${bar._id}`}>
                  <span className={nameClass(checked)}>
                    {bar.shipment_id || bar.display_id || 'To be Planned'}
                  </span>
                </Link>
              </NameContainer>
              <BarContainer opacity={checked ? 1 : 0.3}>
                <ShipmentBarRenderer
                  // @ts-expect-error - TS2322: Type '{ title: any; color: any; DayWidth: any; barIndex: any; totalSiblings: any...
                  title={bar.title}
                  color={color}
                  DayWidth={DayWidth}
                  barIndex={j}
                  totalSiblings={bars.length}
                  data={bar}
                  minDate={minDate}
                  expanded={expanded}
                  {...props}
                />
              </BarContainer>
            </TimelineRow>
          );
        })}
    </>
  );
}

function ShipmentBarRenderer({
  index,
  barIndex,
  totalSiblings,
  providers,
  expanded,
  shipmentUserValue,
  destination_id,
  origin_id,
  DayWidth,
  HeightBar,
  minDate,
  color,
  data,
  showTitle = true,
  setHoverRow,
  activeRow,
}) {
  const daysLeft = useMemo(() => differenceInDays(data.ETD, data.ETA), [data]);
  const depProvider = useMemo(
    () =>
      providers?.find(x => [data.endConnect, data.destination].includes(x._id)),
    [data, providers],
  );
  const destinationIndex = useMemo(
    () =>
      providers?.findIndex(x =>
        [data.endConnect, data.destination].includes(x._id),
      ),
    [data, providers],
  );
  const positionFromLeft = useMemo(
    () => differenceInDays(minDate, data.ETD) * DayWidth,
    [minDate, data],
  );

  const lineSpaceFromLeft = 3;

  const shipmentColor = useMemo(() => {
    if (data?.status === 'prediction') {
      return theme.colors.yellow;
    }

    return depProvider?.color || data.uniqueColor || color;
  }, [color, data, depProvider]);

  const lineHeight = useMemo(() => {
    if (!data.destination) {
      return 0;
    }
    let height = 0;
    const headingHeight = 60;
    const shipmentTitleHeight = 42;
    const shipmentSmallBar = 12;
    const BarHeight = 28;

    const providerIndex = destinationIndex;
    if (providerIndex > -1) {
      if (expanded) {
        height += (totalSiblings - barIndex - 1) * BarHeight;
        height += BarHeight / 2;
      } else {
        height += (totalSiblings - barIndex - 1) * shipmentSmallBar;
        height += shipmentSmallBar / 2;
      }

      if (totalSiblings === 1) {
        height += 12;
      }

      Object.values(shipmentUserValue).forEach((shipmentGroup, i) => {
        if (i > index) {
          height += shipmentTitleHeight;
          // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
          if (shipmentGroup.childrens.length > 1) {
            // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
            if (shipmentGroup.expanded) {
              // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
              height += BarHeight * shipmentGroup.childrens.length;
            } else {
              // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
              height += shipmentSmallBar * shipmentGroup.childrens.length;
            }
          }
        }
      });
      height += headingHeight;
      height += providerIndex * BarHeight;
      return height;
    }
    return 0;
  }, [data, shipmentUserValue, providers]);

  const lineSpaceFromTop = useMemo(() => {
    let height = HeightBar / 2;
    if (totalSiblings === 1) {
      height += 10;
    }
    return height;
  }, [HeightBar, totalSiblings]);

  const active = useMemo(() => {
    if (activeRow) {
      if (
        data.endConnect === activeRow ||
        data.origin_id === activeRow ||
        data._id === activeRow
      ) {
        return true;
      }
      return false;
    }
    return true;
  }, [activeRow]);

  const [elementRect, setElementRect] = useState();

  const isOverflown = useMemo(() => {
    if (!elementRect) return false;

    const element = elementRect;
    return (
      // @ts-expect-error - TS2339: Property 'scrollHeight' does not exist on type 'never'.
      element.scrollHeight > element.clientHeight ||
      // @ts-expect-error - TS2339: Property 'scrollWidth' does not exist on type 'never'.
      element.scrollWidth > element.clientWidth
    );
  }, [elementRect, data]);

  const handleRect = useCallback(node => {
    setElementRect(node);
  }, []);

  return (
    <>
      {destinationIndex > -1 ? (
        <>
          <HorizontalLine
            opacity={active ? 1 : 0.4}
            color={shipmentColor}
            spaceFromTop={lineSpaceFromTop}
            width={lineSpaceFromLeft}
            left={positionFromLeft + DayWidth * daysLeft}
          />
          <VerticalLine
            opacity={active ? 1 : 0.4}
            height={lineHeight}
            color={shipmentColor}
            spaceFromTop={lineSpaceFromTop}
            left={positionFromLeft + lineSpaceFromLeft + DayWidth * daysLeft}
          />
          <ArrowHead
            opacity={active ? 1 : 0.4}
            color={shipmentColor}
            spaceFromTop={lineSpaceFromTop + lineHeight - 8}
            left={
              positionFromLeft + lineSpaceFromLeft - 3 + DayWidth * daysLeft
            }
          />
        </>
      ) : (
        <></>
      )}
      <ShipmentBar
        onMouseEnter={() => setHoverRow(data._id)}
        onMouseLeave={() => setHoverRow(null)}
        bgColor={shipmentColor}
        left={positionFromLeft}
        HeightBar={HeightBar}
        width={DayWidth * daysLeft + (data.clipped ? 3 : 0)}
        opacity={active ? 1 : 0.4}
        isClipped={data.clipped}
        className={data.className || ''}
      >
        {showTitle && (
          <>
            {isValidElement(data.titleRenderer) ? (
              cloneElement(data.titleRenderer)
            ) : data?.status === 'prediction' ? (
              <a
                target="_blank"
                href={`/app/scm/shipments/create/?origin_id=${origin_id}&destination=${destination_id}&type=Fulfillment Provider&step=1`}
                rel="noreferrer"
              >
                Create Shipment in {daysLeft} Days
              </a>
            ) : (
              <BarName>
                <span>{data.title}</span>
                {isValidElement(data.infoContent) &&
                  cloneElement(data.infoContent)}
              </BarName>
            )}
            {(isOverflown || !!data.popupContent) && (
              <FullText>
                {isValidElement(data.titleRenderer) ? (
                  cloneElement(data.titleRenderer, { ref: handleRect })
                ) : data?.status === 'prediction' ? (
                  <a
                    ref={handleRect}
                    target="_blank"
                    href={`/app/scm/shipments/create/?origin_id=${origin_id}&destination=${destination_id}&type=Fulfillment Provider&step=1`}
                    rel="noreferrer"
                  >
                    Create Shipment in {daysLeft} Days
                  </a>
                ) : (
                  <BarName ref={handleRect}>
                    <span>{data.popupContent || data.title}</span>
                  </BarName>
                )}
              </FullText>
            )}
          </>
        )}
      </ShipmentBar>
    </>
  );
}

export function NormalBarRenderer({
  activeRow,
  labelStyles,
  setHoverRow,
  last,
  data,
  getBarColor,
  differenceInDates,
  DayWidth,
  name,
  ...props
}) {
  const shipmentColor = useMemo(() => {
    if (data?.status === 'prediction') {
      return data.color || predictionColor;
    }
    return data.color || getBarColor(data._id);
  }, [data]);

  const onHover = () => {
    setHoverRow(data._id || data.endConnect);
  };

  const unHover = () => {
    setHoverRow(null);
  };

  const active = useMemo(() => {
    if (activeRow) {
      if (data._id === activeRow || data.endConnect === activeRow) {
        return true;
      }
      return false;
    }
    return true;
  }, [activeRow, data]);

  return (
    <TimelineRow width={differenceInDates * DayWidth + 208}>
      <NameContainer last={last} className="sk:pl-6" style={labelStyles}>
        {isValidElement(data.labelRenderer) ? (
          cloneElement(data.labelRenderer)
        ) : (
          <>
            <Circle bgColor={shipmentColor} />
            <Link to={data.link}>
              <span className={nameClass(true)}>{data.name || name}</span>
            </Link>
          </>
        )}
      </NameContainer>
      <BarContainer opacity={active ? 1 : 0.4}>
        <BarRenderer
          data={data}
          shipmentColor={shipmentColor}
          last={last}
          onHover={onHover}
          unHover={unHover}
          // @ts-expect-error - TS2322: Type '{ data: any; shipmentColor: any; last: any; onHover: () => void; unHover: ...
          differenceInDates={differenceInDates}
          DayWidth={DayWidth}
          {...props}
        />
        {data.siblings?.map(sibling => (
          <BarRenderer
            // @ts-expect-error - TS2322: Type '{ differenceInDates: any; DayWidth: any; data: any; shipmentColor: any; la...
            differenceInDates={differenceInDates}
            DayWidth={DayWidth}
            data={sibling}
            shipmentColor={shipmentColor}
            last={last}
            onHover={onHover}
            unHover={unHover}
            {...props}
          />
        ))}
      </BarContainer>
    </TimelineRow>
  );
}

function BarRenderer({
  unHover,
  shipmentUserValue,
  onHover,
  data,
  index,
  context,
  additionalHeight,
  DayWidth,
  HeightBar,
  shipmentColor,
  minDate,
  last,
  maxDate,
  providers,
  shipments,
  purchaseOrders,
}) {
  const daysLeft = useMemo(
    () =>
      differenceInDays(data.startDate, data.infinite ? maxDate : data.endDate),
    [data],
  );
  const positionFromLeft = useMemo(
    () => differenceInDays(minDate, data.startDate) * DayWidth,
    [minDate, data],
  );
  const finalColor = useMemo(() => {
    let depColor = null;
    if (data.endConnect && context !== 'provider') {
      let depShipment = null;
      shipments.forEach(shipmentGroup => {
        depShipment =
          shipmentGroup.shipments?.find(
            shipment =>
              shipment.po_display_id === data.dep ||
              shipment.origin_details?._id === data.supplier?._id ||
              shipment._id === data.endConnect,
          ) || depShipment;
      });
      if (depShipment) {
        const depProvider = providers?.find(x =>
          // @ts-expect-error - TS18047: 'depShipment' is possibly 'null'.
          [depShipment.endConnect, depShipment.destination].includes(x._id),
        );
        if (depProvider) {
          depColor = depProvider.color;
        } else {
          // @ts-expect-error - TS2339: Property 'color' does not exist on type 'never'.
          depColor = depShipment.color;
        }
      }
    }
    return depColor || data.uniqueColor || shipmentColor;
  }, [data]);

  const [elementRect, setElementRect] = useState();

  const isOverflown = useMemo(() => {
    if (data.isOverflown) return true;

    if (!elementRect) return false;

    const element = elementRect;
    // @ts-expect-error - TS2339: Property 'scrollWidth' does not exist on type 'never'.
    return element.scrollWidth > element.clientWidth;
  }, [elementRect, data]);

  const handleRect = useCallback(node => {
    setElementRect(node);
  }, []);

  return data.gap ? (
    <GapRenderer
      data={data}
      positionFromLeft={positionFromLeft}
      HeightBar={HeightBar}
      left={positionFromLeft}
      shipmentColor={shipmentColor}
      last={last}
      DayWidth={DayWidth}
      daysLeft={daysLeft}
      onMouseEnter={onHover}
      onMouseLeave={unHover}
    />
  ) : (
    <>
      <ShipmentBar
        onMouseEnter={onHover}
        onMouseLeave={unHover}
        last={last}
        bgColor={finalColor}
        left={positionFromLeft}
        HeightBar={HeightBar}
        width={DayWidth * daysLeft}
        className={data.className || ''}
        onClick={() => {
          !!data.onClick && data.onClick();
        }}
      >
        <BarName ref={handleRect}>
          {isValidElement(data.title) ? cloneElement(data.title) : data.title}
        </BarName>
        {isOverflown && (
          <FullText>
            {isValidElement(data.title) ? cloneElement(data.title) : data.title}
          </FullText>
        )}
      </ShipmentBar>
      {context === 'PO' ? (
        <PurchaseOrderLine
          providers={providers}
          shipments={shipments}
          positionFromLeft={positionFromLeft}
          data={data}
          index={index}
          HeightBar={HeightBar}
          color={finalColor}
          shipmentUserValue={shipmentUserValue}
          purchaseOrders={purchaseOrders}
          daysLeft={daysLeft}
          barWidth={DayWidth * daysLeft}
          DayWidth={DayWidth}
          additionalHeight={additionalHeight}
        />
      ) : (
        <ProviderLine
          providers={providers}
          // @ts-expect-error - TS2322: Type '{ providers: any; shipments: any; positionFromLeft: number; data: any; ind...
          shipments={shipments}
          positionFromLeft={positionFromLeft}
          data={data}
          index={index}
          HeightBar={HeightBar}
          color={finalColor}
          shipmentUserValue={shipmentUserValue}
          purchaseOrders={purchaseOrders}
          daysLeft={daysLeft}
          DayWidth={DayWidth}
        />
      )}
    </>
  );
}

function PurchaseOrderLine({
  providers,
  additionalHeight = 0,
  DayWidth,
  daysLeft,
  positionFromLeft,
  shipments,
  data,
  color,
  index,
  purchaseOrders,
  shipmentUserValue,
  HeightBar,
  barWidth,
}) {
  const lineStretch = useMemo(() => {
    if (!data.dep) return 0;
    let depShipment = null;
    shipments.forEach(shipmentGroup => {
      depShipment =
        shipmentGroup.shipments?.find(
          shipment =>
            shipment.po_display_id === data.dep ||
            shipment.origin_details?._id === data.supplier?._id ||
            shipment._id === data.endConnect,
        ) || depShipment;
    });
    const depProvider = providers?.find(x => x._id === data.endConnect);
    const endConnect = depProvider || depShipment;
    if (endConnect) {
      return (
        differenceInDays(data.endDate, endConnect.startDate) * DayWidth + 5
      );
    }
    return 10;
  }, [data, providers, shipmentUserValue]);

  const lineHeight = useMemo(() => {
    if (!data.dep) return 0;
    const providerIndex = providers?.findIndex(x => x._id === data.endConnect);
    const lastBarHeight = 32;
    const headingHeight = 60;
    const ShipmentHeadingHeight = 50;
    const shipmentTitleHeight = 42;
    const shipmentSmallBar = 12;
    const BarHeight = 28;
    let depShipment = null;

    const shipmentIndex = shipments?.findIndex(shipmentGroup => {
      depShipment = shipmentGroup.shipments?.find(
        shipment =>
          shipment.po_display_id === data.dep ||
          shipment.origin_details?._id === data.supplier?._id ||
          shipment.origin_id === data._id,
      );
      return depShipment;
    });
    let height = 0;

    if (providerIndex > -1) {
      if (CommonUtility.isValidObject(shipmentUserValue)) {
        height += headingHeight;
        Object.values(shipmentUserValue).forEach(shipmentGroup => {
          height += shipmentTitleHeight;
          // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
          if (shipmentGroup.childrens.length > 1) {
            // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
            if (shipmentGroup.expanded) {
              // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
              height += BarHeight * shipmentGroup.childrens.length;
            } else {
              // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
              height += shipmentSmallBar * shipmentGroup.childrens.length;
            }
          }
        });
      }

      height += headingHeight;
      height -= 12;
      height += additionalHeight;
      height += providerIndex * BarHeight;
    } else if (shipmentIndex > -1) {
      height +=
        (purchaseOrders.length - (index + 1)) * BarHeight + lastBarHeight;
      height += ShipmentHeadingHeight;
      const shipmentsArray = Object.values(shipmentUserValue);

      for (
        let currentIndex = 0;
        currentIndex < shipmentsArray.length;
        currentIndex += 1
      ) {
        const shipmentGroup = shipmentsArray[currentIndex];

        if (currentIndex === shipmentIndex) {
          // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
          if (depShipment && shipmentGroup.childrens.length > 1) {
            height += shipmentTitleHeight;

            // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
            for (const shipment of shipmentGroup.childrens) {
              // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
              if (shipment._id === depShipment._id) {
                break;
              }
              // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
              if (shipmentGroup.expanded) {
                height += BarHeight;
              } else {
                height += shipmentSmallBar;
              }
            }
          }

          break;
        }
        height += shipmentTitleHeight;

        // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
        if (shipmentGroup.childrens.length > 1) {
          // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
          if (shipmentGroup.expanded) {
            // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
            height += BarHeight * shipmentGroup.childrens.length;
          } else {
            // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
            height += shipmentSmallBar * shipmentGroup.childrens.length;
          }
        }
      }
      height += 5;
    }
    return height;
  }, [data, providers, shipmentUserValue]);

  const leftSideLineWidth = -lineStretch - barWidth;

  return data.dep && lineHeight > 0 ? (
    <>
      {lineStretch >= 0 ? (
        <HorizontalLine
          color={color}
          spaceFromTop={HeightBar / 2 + 2 + additionalHeight}
          width={lineStretch}
          left={positionFromLeft + barWidth}
        />
      ) : (
        <HorizontalLine
          color={color}
          spaceFromTop={HeightBar / 2 + 2 + additionalHeight}
          width={leftSideLineWidth}
          left={positionFromLeft - leftSideLineWidth}
        />
      )}
      <VerticalLine
        height={lineHeight}
        color={color}
        spaceFromTop={HeightBar / 2 + 2 + additionalHeight}
        left={positionFromLeft + lineStretch + DayWidth * daysLeft}
      />
      <ArrowHead
        color={color}
        spaceFromTop={HeightBar / 2 + lineHeight + additionalHeight - 4}
        left={positionFromLeft + DayWidth * daysLeft + (lineStretch - 3)}
      />
    </>
  ) : (
    <></>
  );
}

function ProviderLine({
  providers,
  positionFromLeft,
  data,
  color,
  index,
  shipmentUserValue,
  HeightBar,
}) {
  const lineHeight = useMemo(() => {
    if (!data.endConnect) return 0;

    const headingHeight = 48;
    const shipmentTitleHeight = 42;
    const shipmentSmallBar = 12;
    const BarHeight = 28;

    let height = 0;

    height += index * BarHeight;
    height += BarHeight / 2;
    height += headingHeight;
    let found = false;
    Object.values(shipmentUserValue).forEach(shipmentGroup => {
      if (found) {
        height += shipmentTitleHeight;
      }
      // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
      shipmentGroup.childrens.forEach(shipment => {
        // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
        if (found && shipmentGroup.childrens.length > 1) {
          // @ts-expect-error - TS18046: 'shipmentGroup' is of type 'unknown'.
          if (shipmentGroup.expanded) {
            height += BarHeight;
          } else {
            height += shipmentSmallBar;
          }
        }
        if (data.endConnect === shipment._id) {
          found = true;
        }
      });
    });

    return height;
  }, [data, providers, shipmentUserValue]);

  return data.endConnect ? (
    <>
      <HorizontalLine
        color={color}
        spaceFromTop={HeightBar / 2 + 2}
        width={10}
        left={positionFromLeft - 10}
      />
      <VerticalLine
        height={lineHeight}
        color={color}
        spaceFromTop={HeightBar / 2 + 2 - lineHeight}
        left={positionFromLeft - 10}
      />
      <ArrowHead
        up
        color={color}
        spaceFromTop={9 - lineHeight}
        left={positionFromLeft - 14}
      />
    </>
  ) : (
    <></>
  );
}

const stockGapColor = 'rgba(199, 87, 79, 0.3)';
function GapRenderer({
  data,
  last,
  positionFromLeft,
  HeightBar,
  DayWidth,
  daysLeft,
  ...rest
}) {
  return (
    <ShipmentBar
      bgColor={stockGapColor}
      left={positionFromLeft}
      HeightBar={HeightBar + 4}
      width={DayWidth * daysLeft}
      style={{
        transform: last ? 'translateY(-24px)' : 'translateY(-14px)',
        justifyContent: 'space-between',
      }}
      {...rest}
    >
      <svg
        className="sk:-ml-3"
        width="10"
        height="24"
        viewBox="0 0 10 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.5 29L8.5 1"
          stroke="#FF4218"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M1.97717 0H8.8C9.1866 0 9.5 0.313401 9.5 0.7V9.03901C9.5 9.69686 8.67481 9.99143 8.25823 9.48228L1.4354 1.14327C1.06145 0.686215 1.38663 0 1.97717 0Z"
          fill="#FF4218"
        />
      </svg>
      <LightPopup
        trigger={
          <BarName className="sk:text-danger">
            <span>
              {isValidElement(data.title)
                ? cloneElement(data.title)
                : data.title}
            </span>
          </BarName>
        }
        padding="2px 10px"
        basic
      >
        {isValidElement(data.title) ? cloneElement(data.title) : data.title}
      </LightPopup>
      <svg
        className="sk:-mr-3 sk:[transform:rotateY(180deg)]"
        width="10"
        height="24"
        viewBox="0 0 10 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.5 29L8.5 1"
          stroke="#FF4218"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M1.97717 0H8.8C9.1866 0 9.5 0.313401 9.5 0.7V9.03901C9.5 9.69686 8.67481 9.99143 8.25823 9.48228L1.4354 1.14327C1.06145 0.686215 1.38663 0 1.97717 0Z"
          fill="#FF4218"
        />
      </svg>
    </ShipmentBar>
  );
}
